import {
  closePolygonRing,
  getPlotBaseName,
  groupPlotsBySubFarm,
  isGroupSubFarm,
  parseBoundaryGeoJsonRing,
  parseLandplotPolygon,
  ringToEditablePoints,
} from "./mapMeasurements";

function escapeXml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function coordinatesFromPoints(points, closeRing = false) {
  const list = closeRing
    ? closePolygonRing(ringToEditablePoints(points || []))
    : points || [];
  return list
    .filter(
      (point) =>
        point &&
        Number.isFinite(Number(point.lat)) &&
        Number.isFinite(Number(point.lng))
    )
    .map((point) => `${point.lng},${point.lat},0`)
    .join(" ");
}

function polygonKml(outer, holes = []) {
  const outerCoords = coordinatesFromPoints(outer, true);
  if (!outerCoords) return "";
  const holeXml = (holes || [])
    .map((hole) => {
      const coords = coordinatesFromPoints(hole, true);
      if (!coords) return "";
      return (
        "<innerBoundaryIs><LinearRing><coordinates>" +
        coords +
        "</coordinates></LinearRing></innerBoundaryIs>"
      );
    })
    .join("");
  return (
    "<Polygon><outerBoundaryIs><LinearRing><coordinates>" +
    outerCoords +
    "</coordinates></LinearRing></outerBoundaryIs>" +
    holeXml +
    "</Polygon>"
  );
}

function lineKml(points) {
  const coords = coordinatesFromPoints(points, false);
  if (!coords) return "";
  return "<LineString><coordinates>" + coords + "</coordinates></LineString>";
}

function placemarkKml(name, geometryXml) {
  if (!geometryXml) return "";
  return (
    "<Placemark><name>" +
    escapeXml(name) +
    "</name>" +
    geometryXml +
    "</Placemark>"
  );
}

function folderKml(name, placemarks) {
  const items = (placemarks || []).filter(Boolean);
  if (!items.length) return "";
  return (
    "<Folder><name>" +
    escapeXml(name) +
    "</name>" +
    items.join("") +
    "</Folder>"
  );
}

function parsePlotPolygon(plot) {
  if (!plot) return { outer: [], holes: [] };
  if (plot.geomap && typeof plot.geomap === "object") {
    return parseLandplotPolygon({
      ...plot,
      geomap: JSON.stringify(plot.geomap),
    });
  }
  return parseLandplotPolygon(plot);
}

function parseFarmRing(farm) {
  let geo = farm && farm.boundary_geojson;
  if (typeof geo === "string") {
    try {
      geo = JSON.parse(geo);
    } catch (error) {
      return [];
    }
  }
  return parseBoundaryGeoJsonRing(geo);
}

function linePoints(line) {
  if (line && Array.isArray(line.ring) && line.ring.length >= 2) {
    return line.ring;
  }
  const wkt = line && (line.path || line.pathWkt);
  if (!wkt) return [];
  const match = String(wkt).match(/LINESTRING\s*\((.+)\)/i);
  if (!match) return [];
  return match[1]
    .split(",")
    .map((part) => {
      const coords = part.trim().split(/\s+/);
      return { lng: Number(coords[0]), lat: Number(coords[1]) };
    })
    .filter(
      (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng)
    );
}

function idSet(ids) {
  return new Set((ids || []).map((id) => String(id)));
}

function plotKeys(group) {
  return ((group && group.plots) || []).map((plot) => String(plot.id));
}

export function emptyKmlSelection() {
  return { farmIds: [], plotIds: [] };
}

export function groupMapsForKmlExport(plots, groupFarms) {
  const groups = groupPlotsBySubFarm(plots, groupFarms, []);
  const seen = new Set(
    groups.filter((group) => group.farm).map((group) => String(group.farm.id))
  );
  (groupFarms || []).filter(isGroupSubFarm).forEach((farm) => {
    if (seen.has(String(farm.id))) return;
    const ring = parseBoundaryGeoJsonRing(farm.boundary_geojson);
    if (ring.length >= 3) {
      groups.push({ farm, plots: [] });
    }
  });
  return groups;
}

export function isFarmFullySelected(group, selection) {
  const farmKey = group && group.farm ? String(group.farm.id) : "";
  const plots = plotKeys(group);
  const selectedPlots = idSet(selection && selection.plotIds);
  if (farmKey && idSet(selection && selection.farmIds).has(farmKey)) {
    return plots.length === 0 || plots.every((id) => selectedPlots.has(id));
  }
  return plots.length > 0 && plots.every((id) => selectedPlots.has(id));
}

export function isFarmPartiallySelected(group, selection) {
  if (isFarmFullySelected(group, selection)) return false;
  const selectedPlots = idSet(selection && selection.plotIds);
  return plotKeys(group).some((id) => selectedPlots.has(id));
}

export function isPlotSelected(plotId, selection) {
  return idSet(selection && selection.plotIds).has(String(plotId));
}

export function toggleFarmInKmlSelection(group, selection) {
  const farmIds = idSet(selection && selection.farmIds);
  const plotIds = idSet(selection && selection.plotIds);
  const farmKey = group && group.farm ? String(group.farm.id) : "";
  const keys = plotKeys(group);
  if (isFarmFullySelected(group, selection)) {
    if (farmKey) farmIds.delete(farmKey);
    keys.forEach((id) => plotIds.delete(id));
  } else {
    if (farmKey) farmIds.add(farmKey);
    keys.forEach((id) => plotIds.add(id));
  }
  return { farmIds: Array.from(farmIds), plotIds: Array.from(plotIds) };
}

export function togglePlotInKmlSelection(group, plotId, selection) {
  const farmIds = idSet(selection && selection.farmIds);
  const plotIds = idSet(selection && selection.plotIds);
  const key = String(plotId);
  const farmKey = group && group.farm ? String(group.farm.id) : "";
  if (plotIds.has(key)) {
    plotIds.delete(key);
    if (farmKey) farmIds.delete(farmKey);
  } else {
    plotIds.add(key);
    if (farmKey && plotKeys(group).every((id) => plotIds.has(id))) {
      farmIds.add(farmKey);
    }
  }
  return { farmIds: Array.from(farmIds), plotIds: Array.from(plotIds) };
}

export function selectAllKmlSelection(groups) {
  return (groups || []).reduce(
    (selection, group) => toggleFarmInKmlSelection(group, selection),
    emptyKmlSelection()
  );
}

export function initialKmlSelection({
  groups = [],
  selectedFarmIds = [],
  selectedPlotIds = [],
} = {}) {
  if (selectedPlotIds && selectedPlotIds.length) {
    return (groups || []).reduce((selection, group) => {
      return (group.plots || []).reduce((current, plot) => {
        if (!selectedPlotIds.map(String).includes(String(plot.id))) {
          return current;
        }
        return togglePlotInKmlSelection(group, plot.id, current);
      }, selection);
    }, emptyKmlSelection());
  }
  if (selectedFarmIds && selectedFarmIds.length) {
    return (groups || []).reduce((selection, group) => {
      if (
        !group.farm ||
        !selectedFarmIds.map(String).includes(String(group.farm.id))
      ) {
        return selection;
      }
      return toggleFarmInKmlSelection(group, selection);
    }, emptyKmlSelection());
  }
  return emptyKmlSelection();
}

export function filterKmlExportItems({
  plots = [],
  groupFarms = [],
  selection,
} = {}) {
  const plotIds = idSet(selection && selection.plotIds);
  const farmIds = idSet(selection && selection.farmIds);
  return {
    plots: (plots || []).filter((plot) => plotIds.has(String(plot.id))),
    groupFarms: (groupFarms || []).filter((farm) =>
      farmIds.has(String(farm.id))
    ),
  };
}

export function farmMapKmlFilename(farmId) {
  const id = farmId != null && String(farmId).trim() ? String(farmId) : "fazenda";
  return "mapa-fazenda-" + id + ".kml";
}

export function buildFarmMapKml({
  plots = [],
  groupFarms = [],
  lines = [],
  documentName = "Mapa da fazenda",
} = {}) {
  const plotMarks = (plots || []).map((plot) => {
    const parsed = parsePlotPolygon(plot);
    return placemarkKml(
      getPlotBaseName(plot) || plot.name || "",
      polygonKml(parsed.outer, parsed.holes)
    );
  });

  const farmMarks = (groupFarms || []).filter(isGroupSubFarm).map((farm) => {
    const ring = parseFarmRing(farm);
    return placemarkKml(farm.nome_da_fazenda || "", polygonKml(ring));
  });

  const lineMarks = (lines || []).map((line) =>
    placemarkKml(line.name || "Linha", lineKml(linePoints(line)))
  );

  const body =
    folderKml("Talhões", plotMarks) +
    folderKml("Sub-fazendas", farmMarks) +
    folderKml("Linhas", lineMarks);

  return (
    '<?xml version="1.0" encoding="UTF-8"?>' +
    '<kml xmlns="http://www.opengis.net/kml/2.2">' +
    "<Document><name>" +
    escapeXml(documentName) +
    "</name>" +
    body +
    "</Document></kml>"
  );
}

export function farmMapKmlHasGeometry(kmlText) {
  return /<(Polygon|LineString)>/i.test(kmlText || "");
}

export function downloadKmlFile(kmlText, filename) {
  if (typeof document === "undefined") return false;
  const blob = new Blob([kmlText], {
    type: "application/vnd.google-earth.kml+xml;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "mapa.kml";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
}
